/**
 * @file     WishlistContext.tsx
 * @purpose  React context and reducer providing Liked-properties state (add/remove/has/clear) for a discriminated union of curated slugs and MLS listing keys. Persists to localStorage under v3 key with one-time migration from legacy v2 and v1 keys.
 *
 *           Note: "wishlist" is the legacy internal name kept for stability of
 *           the React hook + context API surface. The user-facing label
 *           everywhere in the product is "Liked properties" / "Like this home".
 */
'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';

// Current key — "Liked properties" rebrand (PR-C of /properties sprint).
// Migration on read: if v3 missing but v2 exists, promote v2 → v3 and delete v2.
// Older v1 (session+local) also still merged on read for one more cycle.
const STORAGE_KEY_V3 = 'relta.likedProperties.v3';
const STORAGE_KEY_V2 = 'relta.wishlist.v2';
const STORAGE_KEY_V1 = 'relta_wishlist_v1';

export type WishlistEntry =
  | { source: 'curated'; slug: string; savedAt: string }
  | { source: 'mls'; listingKey: string; savedAt: string };

export type WishlistInput =
  | { source: 'curated'; slug: string }
  | { source: 'mls'; listingKey: string };

type WishlistState = {
  entries: WishlistEntry[];
};

type WishlistAction =
  | { type: 'ADD'; input: WishlistInput }
  | { type: 'REMOVE'; input: WishlistInput }
  | { type: 'HYDRATE'; entries: WishlistEntry[] };

const entryKey = (e: WishlistEntry | WishlistInput): string =>
  e.source === 'curated' ? `curated:${e.slug}` : `mls:${e.listingKey}`;

const reducer = (state: WishlistState, action: WishlistAction): WishlistState => {
  switch (action.type) {
    case 'ADD': {
      const key = entryKey(action.input);
      if (state.entries.some((e) => entryKey(e) === key)) return state;
      const savedAt = new Date().toISOString();
      const entry: WishlistEntry =
        action.input.source === 'curated'
          ? { source: 'curated', slug: action.input.slug, savedAt }
          : { source: 'mls', listingKey: action.input.listingKey, savedAt };
      return { entries: [...state.entries, entry] };
    }
    case 'REMOVE': {
      const key = entryKey(action.input);
      return { entries: state.entries.filter((e) => entryKey(e) !== key) };
    }
    case 'HYDRATE':
      return { entries: action.entries };
    default:
      return state;
  }
};

type V2RawEntry = { source?: string; slug?: string; listingKey?: string; savedAt?: string };

export function normalizeStoredEntries(raw: unknown): WishlistEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: WishlistEntry[] = [];
  const seen = new Set<string>();
  for (const item of raw as V2RawEntry[]) {
    if (!item || typeof item !== 'object') continue;
    const savedAt = typeof item.savedAt === 'string' ? item.savedAt : new Date().toISOString();
    if (item.source === 'curated' && typeof item.slug === 'string' && item.slug) {
      const k = `curated:${item.slug}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ source: 'curated', slug: item.slug, savedAt });
    } else if (item.source === 'mls' && typeof item.listingKey === 'string' && item.listingKey) {
      const k = `mls:${item.listingKey}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ source: 'mls', listingKey: item.listingKey, savedAt });
    }
  }
  return out;
}

export function migrateLegacyV1(raw: unknown): WishlistEntry[] {
  // Legacy v1 shape: [{ id: string, savedAt: string }, ...] — id was the curated PropertyListing.id
  // (callers historically passed listing.id). We promote those to curated slugs by best-effort:
  // since v1 stored ids and curated entries are now keyed by slug, we still write them as curated
  // entries using the stored value as the slug. The /properties page render filters out anything
  // that doesn't match a real curated row, so stale v1 entries from previous browsers are
  // harmless — they just won't render.
  if (!Array.isArray(raw)) return [];
  const out: WishlistEntry[] = [];
  const seen = new Set<string>();
  for (const item of raw as Array<{ id?: string; savedAt?: string }>) {
    if (!item || typeof item.id !== 'string' || !item.id) continue;
    const k = `curated:${item.id}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({
      source: 'curated',
      slug: item.id,
      savedAt: typeof item.savedAt === 'string' ? item.savedAt : new Date().toISOString(),
    });
  }
  return out;
}

export function mergeEntries(a: WishlistEntry[], b: WishlistEntry[]): WishlistEntry[] {
  const map = new Map<string, WishlistEntry>();
  for (const e of [...a, ...b]) {
    const k = entryKey(e);
    if (!map.has(k)) map.set(k, e);
  }
  return Array.from(map.values());
}

const readFromStorage = (): WishlistEntry[] => {
  if (typeof window === 'undefined') return [];
  let v3: WishlistEntry[] = [];
  let v2: WishlistEntry[] = [];
  let v1Local: WishlistEntry[] = [];
  let v1Session: WishlistEntry[] = [];
  try {
    const rawV3 = window.localStorage.getItem(STORAGE_KEY_V3);
    if (rawV3) v3 = normalizeStoredEntries(JSON.parse(rawV3));
  } catch {
    // ignore
  }
  try {
    const rawV2 = window.localStorage.getItem(STORAGE_KEY_V2);
    if (rawV2) v2 = normalizeStoredEntries(JSON.parse(rawV2));
  } catch {
    // ignore
  }
  try {
    const rawV1 = window.localStorage.getItem(STORAGE_KEY_V1);
    if (rawV1) v1Local = migrateLegacyV1(JSON.parse(rawV1));
  } catch {
    // ignore
  }
  try {
    const rawV1s = window.sessionStorage?.getItem(STORAGE_KEY_V1);
    if (rawV1s) v1Session = migrateLegacyV1(JSON.parse(rawV1s));
  } catch {
    // ignore
  }
  return mergeEntries(mergeEntries(mergeEntries(v3, v2), v1Local), v1Session);
};

const writeToStorage = (entries: WishlistEntry[]) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY_V3, JSON.stringify(entries));
  } catch {
    // ignore
  }
};

const clearLegacyKeys = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY_V2);
  } catch {
    // ignore
  }
  try {
    window.localStorage.removeItem(STORAGE_KEY_V1);
  } catch {
    // ignore
  }
  try {
    window.sessionStorage?.removeItem(STORAGE_KEY_V1);
  } catch {
    // ignore
  }
};

export type WishlistContextValue = {
  entries: WishlistEntry[];
  add: (input: WishlistInput) => void;
  remove: (input: WishlistInput) => void;
  has: (input: WishlistInput) => boolean;
  count: number;
  curatedSlugs: string[];
  mlsListingKeys: string[];
};

export const WishlistContext = createContext<WishlistContextValue | undefined>(undefined);

export const WishlistProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, { entries: [] });

  useEffect(() => {
    const saved = readFromStorage();
    if (saved.length > 0) {
      dispatch({ type: 'HYDRATE', entries: saved });
    }
    // Clear v1 keys after merge so we don't keep re-hydrating from legacy.
    clearLegacyKeys();
  }, []);

  useEffect(() => {
    writeToStorage(state.entries);
  }, [state.entries]);

  const add = useCallback((input: WishlistInput) => dispatch({ type: 'ADD', input }), []);
  const remove = useCallback((input: WishlistInput) => dispatch({ type: 'REMOVE', input }), []);
  const has = useCallback(
    (input: WishlistInput) => {
      const key = entryKey(input);
      return state.entries.some((e) => entryKey(e) === key);
    },
    [state.entries],
  );

  const { curatedSlugs, mlsListingKeys } = useMemo(() => {
    const c: string[] = [];
    const m: string[] = [];
    for (const e of state.entries) {
      if (e.source === 'curated') c.push(e.slug);
      else m.push(e.listingKey);
    }
    return { curatedSlugs: c, mlsListingKeys: m };
  }, [state.entries]);

  return (
    <WishlistContext.Provider
      value={{
        entries: state.entries,
        add,
        remove,
        has,
        count: state.entries.length,
        curatedSlugs,
        mlsListingKeys,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlistContext = (): WishlistContextValue => {
  const ctx = useContext(WishlistContext);
  if (!ctx) {
    throw new Error('useWishlistContext must be used inside WishlistProvider');
  }
  return ctx;
};
